import type { FormSchema, GeneratorOptions } from '@cli-types/core-engine/schema.js'
import type { StepResult } from '@cli-types/next-adapter/forms.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import { toCamelCase, toPascalCase } from '@cli-utils/text.js'
import { getAllFormSchemaFields } from '@core-engine/schema/field-helpers/form-fields/get-all-form-schema-fields.js'
import { hasDynamicFields } from '@core-engine/schema/field-helpers/form-fields/has-dynamic-fields.js'
import { formFieldToDrizzleType } from '@core-engine/schema/type-mappers/drizzle/form-field-to-drizzle-type.js'
import { resolveExtensionNamespace } from '@next-adapter/config/paths/resolve-extension-namespace.js'
import { renderFormDatabaseImportsTemplate } from '@next-adapter/templates/renderers/generators/form-pipeline/form-database.js'

export function generateFormDatabase(
  schema: FormSchema,
  dbDir: string,
  options: GeneratorOptions
): StepResult {
  const outputNamespace = resolveExtensionNamespace(schema.name, options.outputNamespace)
  const tableName = `${toCamelCase(schema.name)}Submissions`
  const fields = getAllFormSchemaFields(schema)
  const includeDynamic = hasDynamicFields(schema)

  const requiredImports = new Set<string>(['pgTable', 'timestamp', 'text', 'uuid'])
  if (includeDynamic) {
    requiredImports.add('jsonb')
  }

  const fieldDefs = fields
    .map((field) => {
      const drizzleType = formFieldToDrizzleType(field, requiredImports)
      return `  ${field.name}: ${drizzleType},`
    })
    .join('\n')

  const customFieldsCol = includeDynamic
    ? '\n  customFields: jsonb().$type<Record<string, unknown>>(),'
    : ''

  const imports = Array.from(requiredImports).sort().join(',\n  ')
  const content = renderFormDatabaseImportsTemplate({
    imports: imports,
    tableName: tableName,
    toPascalCase: toPascalCase(schema.name),
    fieldDefs: fieldDefs,
    customFieldsCol: customFieldsCol
  })

  return {
    files: [createGeneratedFile(`${dbDir}/${outputNamespace}/${schema.name}.ts`, content)]
  }
}
